import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ChangeEvent, ReactElement } from 'react'
import type { ITask } from '~/types/app-setup'

import { MODAL_ID } from '..'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useCreateTaskMutation, useUpdateTaskMutation } from '~/queries/application-setup'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { authModes } from '~/utils/react-flow'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const authModeOptions = authModes.map((authMode) => ({
  label: authMode,
  value: authMode,
}))

interface FormValues {
  auth_mode: string
  description: string
  id: string
  url: string
}

const validationSchema = Yup.object().shape({
  auth_mode: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  description: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  id: Yup.string()
    .trim()
    .required(FORM_ERRORS.REQUIRED)
    .matches(/^[a-zA-Z0-9_-]+$/, FORM_ERRORS.INVALID_CHARS('a-zA-Z0-9_-')),
  url: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  onClose: VoidFunction
  onSuccess: (task: ITask) => void
  task?: ITask
}

const CreateModal = ({ onClose, onSuccess, task }: Props): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      auth_mode: authModes[0],
      description: task?.description || '',
      id: task?.id || '',
      url: task?.url || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const watchDescription = watch('description')

  /** Queries & Mutations */

  const { mutateAsync: createTaskMutation } = useCreateTaskMutation()

  const { mutateAsync: updateTaskMutation } = useUpdateTaskMutation()

  /** Handlers */

  const handleDescriptionChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
    setValue('description', evt.target.value, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const newTask = task
        ? await updateTaskMutation({
            payload: {
              ...values,
              previous_task_ids: task.previous_task_ids,
            },
          })
        : await createTaskMutation({
            ...values,
            previous_task_ids: [],
          })

      onSuccess(newTask)
    } catch (e) {
      displayToast(`The task could not be ${task ? 'updated' : 'created'}!`, 'error')
      captureException(e)
    }
  }

  return (
    <div className="create-task-modal">
      <ModalHeader>{task ? 'Update the task' : 'Create a new task'}</ModalHeader>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody className="create-task-modal-body">
          <RequiredInformation />
          <TextInput
            {...register('id')}
            disabled={!!task}
            error={errors.id?.message}
            label="ID"
            placeholder="ID"
            required
          />
          <TextArea
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            onChange={handleDescriptionChange}
            placeholder="Enter a description"
            required
            value={watchDescription}
          />
          <TextInput {...register('url')} error={errors.url?.message} label="Url" placeholder="Enter an url" required />
          <Controller
            control={control}
            name="auth_mode"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.auth_mode?.message}
                label="Authentication mode"
                // NOTE: need to get all auth modes
                options={authModeOptions}
                placeholder="Select an authentication mode"
                required
                value={authModeOptions.find(({ value }) => value === field.value)}
              />
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            {task ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
      <DevTool control={control} />
    </div>
  )
}

export default CreateModal
